import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, Badge, Skeleton } from '@rackd/ui';
import { IconInbox, IconCheck, IconX } from '@tabler/icons-react';
import { useReviewQueue, SubmissionCard } from '@/features/review-queue';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const }
  },
};

type StatusFilter = 'pending' | 'approved' | 'rejected';

const statusTabs: { value: StatusFilter; label: string; icon: typeof IconInbox }[] = [
  { value: 'pending', label: 'Pending', icon: IconInbox },
  { value: 'approved', label: 'Approved', icon: IconCheck },
  { value: 'rejected', label: 'Rejected', icon: IconX },
];

export function ReviewsPage() {
  const [status, setStatus] = useState<StatusFilter>('pending');
  const { submissions, total, isLoading, error } = useReviewQueue({ status });

  return (
    <motion.div
      className="space-y-6"
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--text)]">Review Queue</h1>
        <p className="text-sm text-[var(--text-tertiary)] mt-1">
          Review and approve community program submissions
        </p>
      </div>

      {/* Status tabs */}
      <div className="flex items-center gap-2 border-b border-[var(--border)] pb-4">
        {statusTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = status === tab.value;
          return (
            <button
              key={tab.value}
              onClick={() => setStatus(tab.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-[var(--accent-primary)] text-white'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--background-tertiary)]'
              }`}
            >
              <Icon size={18} />
              {tab.label}
              {tab.value === 'pending' && total > 0 && (
                <Badge className="bg-white/20 text-white ml-1">{total}</Badge>
              )}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4">
              <div className="space-y-3">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-full" />
                <div className="flex gap-4">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="p-8 text-center">
          <p className="text-[var(--error)]">Error loading submissions</p>
          <p className="text-sm text-[var(--text-tertiary)] mt-1">{error.message}</p>
        </Card>
      ) : submissions.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="w-16 h-16 bg-[var(--background-tertiary)] rounded-full flex items-center justify-center mx-auto mb-4">
            <IconInbox size={32} className="text-[var(--text-tertiary)]" />
          </div>
          <h3 className="text-lg font-semibold text-[var(--text)] mb-1">
            {status === 'pending' ? 'No pending submissions' : `No ${status} submissions`}
          </h3>
          <p className="text-sm text-[var(--text-tertiary)]">
            {status === 'pending'
              ? 'All caught up! Check back later for new submissions.'
              : `There are no ${status} program submissions to display.`}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {submissions.map((submission) => (
            <SubmissionCard key={submission.id} submission={submission} />
          ))}
        </div>
      )}
    </motion.div>
  );
}
